import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import React from 'react'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Checkbox from 'core/elements/input/Checkbox'
import Radio from 'core/elements/input/Radio'
import Theme from 'core/themes/model'
import { noop } from 'utils/fp'
import { memoizeShallow } from 'utils/misc'
import generateTestId from 'utils/test-helpers'
import GridRowMenu from './GridRowMenu'
import { GridRowMenuItemsProps, GridRowMenuOffset } from './hooks/useGridRowMenu'
import { SelectableParsedGridRow } from './hooks/useGridSelectableRows'

interface SelectRowColumnProps {
  className: string
  multiSelection: boolean
  isSelectable: boolean
  isSelected: boolean
  onClick?: () => void
}

const SelectRowColumn = memoizeShallow(
  function SelectRowColumn({
    className,
    multiSelection,
    isSelectable,
    isSelected,
    onClick,
  }: SelectRowColumnProps) {
    if (isSelectable === undefined) {
      return null
    }
    const Toggler = multiSelection ? Checkbox : Radio
    return (
      <td
        data-testid={generateTestId('cluster', 'checkbox', 'selection')}
        className={className}
        onClick={onClick}
      >
        <Toggler disabled={!isSelectable} checked={isSelected} onChange={noop} />
      </td>
    )
  },
  {
    maxSize: 8,
  },
)

export interface GridRowProps<T> extends SelectableParsedGridRow<T>, GridRowMenuItemsProps<T> {
  className: string
  tdClassName: string
  cellClassName: string
  index?: number
  numPageItems?: number
  rowMenuOffset?: GridRowMenuOffset
  hideCheckboxes?: boolean
  isReadOnly?: boolean
  /** When set, renders a chevron in a last column; returning non-null opens a full width row */
  renderExpandedRow?: (row: T) => React.ReactNode | null
  isExpanded?: boolean
  onToggleExpand?: () => void
}

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    position: 'relative',
  },
  stickied: {
    // CAUTION - Automation may break if we change these values - https://platform9.atlassian.net/browse/PCD-2538
    position: 'sticky',
    right: 0,
    verticalAlign: 'baseline',
    zIndex: 8, // 1 less than the sticky table header
  },
  expandToggleCell: {
    width: 32,
    textAlign: 'center',
  },
  expandToggleButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: theme.spacing(0.5),
  },
}))

export default function GridRow<T>(props: GridRowProps<T>) {
  const {
    isSelectable,
    multiSelection,
    isSelected,
    toggleSelect,
    getCells,
    rowMenuItems = [],
    rowMenuDisabled = !rowMenuItems.length,
    item,
    className,
    tdClassName,
    cellClassName,
    index,
    numPageItems,
    rowMenuOffset = {},
    showRowMenuForSingleRowActions,
    maxRowMenuHeight,
    hideCheckboxes,
    isReadOnly,
    renderExpandedRow,
    isExpanded,
    onToggleExpand,
  } = props
  const classes = useStyles()
  const cells = getCells()
  const hasRowMenuColumn = !isReadOnly && rowMenuItems.length > 0
  const expandedContent = renderExpandedRow ? renderExpandedRow(item) : null
  const hasExpandedContent = expandedContent !== null && expandedContent !== undefined
  const showExpandedRow = !!renderExpandedRow && !!isExpanded && hasExpandedContent
  // column count for the full width expanded row below: selection + data + row menu + expand toggle
  const selectionColSpan = hideCheckboxes ? 0 : 1
  const rowMenuColSpan = hasRowMenuColumn ? 1 : 0
  const expandColSpan = renderExpandedRow ? 1 : 0
  const colSpan = selectionColSpan + cells.length + rowMenuColSpan + expandColSpan
  return (
    <>
      <tr className={clsx(classes.container, className)}>
        {!hideCheckboxes && (
          <SelectRowColumn
            className={tdClassName}
            {...{
              multiSelection,
              isSelectable,
              isSelected,
              onClick: isSelectable ? toggleSelect : undefined,
            }}
          />
        )}
        {cells.map(({ key, CellComponent, value, getFormattedValue }, idx) => {
          const formattedValue = getFormattedValue()
          return (
            <td data-testid={generateTestId(key)} key={key} className={tdClassName}>
              <CellComponent
                index={idx}
                item={item}
                value={value}
                title={String(formattedValue)}
                className={cellClassName}
              >
                {formattedValue}
              </CellComponent>
            </td>
          )
        })}
        {hasRowMenuColumn ? (
          <td className={classes.stickied}>
            <GridRowMenu
              item={item}
              rowMenuItems={rowMenuItems}
              rowMenuDisabled={rowMenuDisabled || !isSelectable}
              rowMenuOffset={
                index === numPageItems - 1
                  ? {
                      vertical: rowMenuOffset.vertical * -1,
                    }
                  : rowMenuOffset
              }
              showRowMenuForSingleRowActions={showRowMenuForSingleRowActions}
              maxRowMenuHeight={maxRowMenuHeight}
              toggleRow={toggleSelect}
            />
          </td>
        ) : null}
        {renderExpandedRow ? (
          <td className={clsx(tdClassName, classes.expandToggleCell)}>
            {hasExpandedContent ? (
              <button
                type="button"
                className={classes.expandToggleButton}
                aria-expanded={!!isExpanded}
                aria-label={isExpanded ? 'Hide details' : 'Show details'}
                onClick={onToggleExpand}
              >
                <FontAwesomeIcon size="sm">
                  {isExpanded ? 'chevron-up' : 'chevron-down'}
                </FontAwesomeIcon>
              </button>
            ) : null}
          </td>
        ) : null}
      </tr>
      {showExpandedRow ? (
        <tr>
          <td colSpan={colSpan} className={tdClassName}>
            {expandedContent}
          </td>
        </tr>
      ) : null}
    </>
  )
}
